import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Loader2, Plus, Trash2 } from 'lucide-react';
import Button from '@components/common/Button';
import GenericModal from '@components/common/modals/GenericModal';
import { Input, Select, Textarea } from '@components/common';
import EvidenceUploadZone from './EvidenceUploadZone';
import { evidenceService } from '@services';
import { EVIDENCE_TYPE_OPTIONS } from '@app-types';
import type { Evidence, EvidenceFile, EvidenceType } from '@app-types';
import { DRAFT_EVIDENCE_LABEL, DRAFT_EVIDENCE_NOT_FOUND_MESSAGE, findDraftEvidence } from '@utils/evidenceVisibility';
import { getApiErrorMessage } from '@utils/apiError';
import { showToast } from '@utils/toast';
import styles from './EvidenceDraftModal.module.css';

interface EvidenceDraftModalProps {
  assessmentItemId: number;
  isOpen: boolean;
  onClose: () => void;
  onChanged?: () => void;
}

interface DraftFormData {
  evidence_type: EvidenceType;
  evidence_name: string;
  evidence_description: string;
  notes: string;
}

const toFormData = (draft: Evidence): DraftFormData => ({
  evidence_type: draft.evidence_type,
  evidence_name: draft.evidence_name,
  evidence_description: draft.evidence_description || '',
  notes: draft.notes || ''
});

/**
 * Edit Draft Evidence before a Contract Manager submits it: metadata, add
 * files, remove files (ADO-1148). Submit and Delete live next to the row.
 */
const EvidenceDraftModal: React.FC<EvidenceDraftModalProps> = ({ assessmentItemId, isOpen, onClose, onChanged }) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<DraftFormData | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [filesToAdd, setFilesToAdd] = useState<File[]>([]);

  const evidenceQueryKey = ['assessment-item-evidence', assessmentItemId];

  const { data, isLoading, error } = useQuery({
    queryKey: evidenceQueryKey,
    queryFn: () => evidenceService.getEvidenceForItem(assessmentItemId),
    enabled: isOpen
  });

  const draft = data ? findDraftEvidence(data.evidence || []) : undefined;

  useEffect(() => {
    if (draft && formData === null) {
      setFormData(toFormData(draft));
    }
  }, [draft, formData]);

  const afterChange = () => {
    void queryClient.invalidateQueries({ queryKey: evidenceQueryKey });
    void queryClient.invalidateQueries({ queryKey: ['contractManagerGapItems'] });
    onChanged?.();
  };

  const saveMutation = useMutation({
    mutationFn: ({ evidenceId, update }: { evidenceId: number; update: DraftFormData }) =>
      evidenceService.updateEvidence(evidenceId, update),
    onSuccess: () => {
      showToast.success('Draft evidence updated');
      afterChange();
      onClose();
    },
    onError: (mutationError: unknown) => {
      setErrors({ submit: getApiErrorMessage(mutationError, 'Failed to update draft evidence') });
    }
  });

  const addFilesMutation = useMutation({
    mutationFn: ({ evidenceId, files }: { evidenceId: number; files: File[] }) =>
      evidenceService.addEvidenceFiles(evidenceId, files),
    onSuccess: () => {
      showToast.success('Files added');
      setFilesToAdd([]);
      afterChange();
    },
    onError: (mutationError: unknown) => {
      showToast.error(getApiErrorMessage(mutationError, 'Failed to add files'));
    }
  });

  const removeFileMutation = useMutation({
    mutationFn: ({ evidenceId, fileId }: { evidenceId: number; fileId: string }) =>
      evidenceService.deleteEvidenceFile(evidenceId, fileId),
    onSuccess: () => {
      showToast.success('File removed');
      afterChange();
    },
    onError: (mutationError: unknown) => {
      showToast.error(getApiErrorMessage(mutationError, 'Failed to remove file'));
    }
  });

  const isBusy = saveMutation.isPending || addFilesMutation.isPending || removeFileMutation.isPending;

  const handleInputChange = (field: keyof DraftFormData, value: string) => {
    setFormData((prev) => (prev ? { ...prev, [field]: value } : prev));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validate = (values: DraftFormData): boolean => {
    const newErrors: Record<string, string> = {};
    if (!values.evidence_name.trim()) {
      newErrors.evidence_name = 'Evidence name is required';
    }
    if (values.evidence_description.trim().length < 10) {
      newErrors.evidence_description = 'Description must be at least 10 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!draft || !formData || !validate(formData)) {
      return;
    }
    saveMutation.mutate({ evidenceId: draft.id, update: formData });
  };

  const handleClose = () => {
    setFormData(null);
    setErrors({});
    setFilesToAdd([]);
    onClose();
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className={styles.loading}>
          <Loader2 size={20} className="animate-spin" />
          Loading draft evidence...
        </div>
      );
    }
    if (error || !draft) {
      return <p className={styles.formError}>{DRAFT_EVIDENCE_NOT_FOUND_MESSAGE}</p>;
    }
    if (!formData) {
      return null;
    }
    return (
      <div className={styles.draftForm}>
        <div className={styles.draftStatus}>
          <FileText size={14} />
          <span>{DRAFT_EVIDENCE_LABEL}</span>
          {draft.uploaded_by_name && <span className={styles.uploadedBy}>Uploaded by {draft.uploaded_by_name}</span>}
        </div>

        <Select
          id="draft_evidence_type"
          label="Evidence Type"
          required
          value={formData.evidence_type}
          onChange={(value) => handleInputChange('evidence_type', value)}
          options={EVIDENCE_TYPE_OPTIONS.map((type) => ({
            value: type.value,
            label: `${type.label} - ${type.description}`
          }))}
          dataTestId="draft-evidence-type"
        />

        <Input
          id="draft_evidence_name"
          label="Evidence Name"
          type="text"
          value={formData.evidence_name}
          onChange={(e) => handleInputChange('evidence_name', e.target.value)}
          required
          error={errors.evidence_name}
          dataTestId="draft-evidence-name"
        />

        <Textarea
          id="draft_evidence_description"
          label="Evidence Description"
          required
          rows={4}
          value={formData.evidence_description}
          onChange={(e) => handleInputChange('evidence_description', e.target.value)}
          error={errors.evidence_description}
          dataTestId="draft-evidence-description"
        />

        <Textarea
          id="draft_notes"
          label="Additional Notes"
          rows={3}
          value={formData.notes}
          onChange={(e) => handleInputChange('notes', e.target.value)}
          dataTestId="draft-notes"
        />

        <div className={styles.filesSection}>
          <span className={styles.sectionLabel}>Files ({draft.files.length})</span>
          {draft.files.length === 0 ? (
            <p className={styles.noFiles}>No files attached.</p>
          ) : (
            <ul className={styles.filesList}>
              {draft.files.map((file: EvidenceFile) => (
                <li key={file.id} className={styles.fileItem} data-test-id="draft-evidence-file">
                  <FileText size={16} />
                  <span className={styles.fileName}>{file.name}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={isBusy}
                    onClick={() => removeFileMutation.mutate({ evidenceId: draft.id, fileId: file.id })}
                    dataTestId="draft-remove-file-button"
                  >
                    <Trash2 size={14} />
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}

          <EvidenceUploadZone
            selectedFiles={filesToAdd}
            onFilesSelect={(files) => setFilesToAdd((prev) => [...prev, ...files])}
            onFileRemove={(index) => setFilesToAdd((prev) => prev.filter((_, i) => i !== index))}
            onValidationError={(message) => setErrors((prev) => ({ ...prev, files: message }))}
            error={errors.files}
            isProcessing={addFilesMutation.isPending}
          />
          {filesToAdd.length > 0 && (
            <Button
              variant="secondary"
              size="sm"
              disabled={isBusy}
              onClick={() => addFilesMutation.mutate({ evidenceId: draft.id, files: filesToAdd })}
              dataTestId="draft-add-files-button"
            >
              <Plus size={14} />
              Add {filesToAdd.length} file{filesToAdd.length === 1 ? '' : 's'}
            </Button>
          )}
        </div>

        {errors.submit && <p className={styles.formError}>{errors.submit}</p>}
      </div>
    );
  };

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Draft Evidence"
      size="lg"
      disableBackdropClick
      dataTestId="evidence-draft-modal"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isBusy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isBusy || !draft || !formData}
            dataTestId="draft-save-button"
          >
            {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </>
      }
    >
      {renderBody()}
    </GenericModal>
  );
};

export default EvidenceDraftModal;
